/**
 * API communication functions for Ragsmith Web UI
 */

import { API_BASE, state, getSubordinateDisabledTools } from './state.js';
import { getAuthToken } from './auth.js';

/**
 * URL for a chat media artifact (video stream / poster frame).
 *
 * A <video src> is fetched by the browser itself and cannot carry headers, so
 * auth rides in the query string — the same trick /voice/tts/mp3/{id}?token=
 * already uses, and the middleware already accepts both forms.
 */
export function mediaUrl(mediaId, kind = 'stream') {
    const params = new URLSearchParams();
    if (state.containerMode && state.containerToken) params.set('u', state.containerToken);
    else if (state.capsuleMode && state.capsuleToken) params.set('c', state.capsuleToken);
    else {
        const token = getAuthToken();
        if (token) params.set('token', token);
    }
    const qs = params.toString();
    return `/chat/media/${mediaId}/${kind}${qs ? `?${qs}` : ''}`;
}

/**
 * Get headers for API requests, including auth token and container token if present
 */
export function getHeaders(includeContentType = true) {
    const headers = {};
    if (includeContentType) {
        headers['Content-Type'] = 'application/json';
    }
    const token = getAuthToken();
    if (token) {
        headers['X-Auth-Token'] = token;
    }
    // Include container token if in container mode
    if (state.containerMode && state.containerToken) {
        headers['X-Container-Token'] = state.containerToken;
    }
    // Include capsule token if in capsule mode
    if (state.capsuleMode && state.capsuleToken) {
        headers['X-Capsule-Token'] = state.capsuleToken;
    }
    return headers;
}

/**
 * Custom error carrying the HTTP status for failed API calls.
 */
export class ApiError extends Error {
    constructor(message, status) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

/**
 * fetch() wrapper that throws a descriptive ApiError on a non-2xx response
 * (pulling the server's `detail` when present) instead of silently passing a
 * broken Response on to res.json(). Returns the Response on success.
 */
export async function apiFetch(url, options) {
    const res = await fetch(url, options);
    if (!res.ok) {
        let detail = `Request failed (${res.status})`;
        try {
            const body = await res.json();
            if (body && body.detail) detail = body.detail;
        } catch {
            // non-JSON error body — keep the generic status message
        }
        throw new ApiError(detail, res.status);
    }
    return res;
}

/**
 * Check API server status
 */
export async function checkStatus() {
    const res = await apiFetch(`${API_BASE}/status`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Quick health check - lightweight ping to check if server is alive
 * @param {string} agent - Optional agent name to check if it's loaded
 * @returns {Promise<{status: string, agent_loaded?: boolean}>}
 */
export async function checkHealth(agent = null) {
    const url = agent
        ? `${API_BASE}/health?agent=${encodeURIComponent(agent)}`
        : `${API_BASE}/health`;
    const res = await apiFetch(url, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch list of available agents
 */
export async function fetchAgents() {
    const res = await apiFetch(`${API_BASE}/agents`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch list of available collections (only those with metadata)
 */
export async function fetchCollections() {
    const res = await apiFetch(`${API_BASE}/collections`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch list of available models (basic tier info for dropdown)
 */
export async function fetchModels() {
    const res = await apiFetch(`${API_BASE}/models`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch all models with full details for Model Manager
 * Returns models grouped by provider with usage stats
 */
export async function fetchAllModels() {
    const res = await apiFetch(`${API_BASE}/models/all`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch model usage statistics with optional filtering
 * @param {string} period - Time period: "today", "week", "month", or "all" (default)
 * @param {string} provider - Optional provider filter: "local", "anthropic", "openrouter"
 */
export async function fetchModelUsage(period = 'all', provider = null, viewContainer = null) {
    const params = new URLSearchParams();
    if (period && period !== 'all') params.set('period', period);
    if (provider && provider !== 'all') params.set('provider', provider);
    if (viewContainer) params.set('view_container', viewContainer);

    const queryString = params.toString();
    const url = queryString ? `${API_BASE}/models/usage?${queryString}` : `${API_BASE}/models/usage`;

    const res = await apiFetch(url, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch rate limits for API providers
 */
export async function fetchRateLimits() {
    const res = await apiFetch(`${API_BASE}/models/rate-limits`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch tool usage analytics
 */
export async function fetchToolUsage(period = 'all', viewContainer = null) {
    const params = new URLSearchParams();
    if (period && period !== 'all') params.set('period', period);
    if (viewContainer) params.set('view_container', viewContainer);
    const qs = params.toString();
    const url = qs ? `${API_BASE}/models/tool-usage?${qs}` : `${API_BASE}/models/tool-usage`;
    const res = await apiFetch(url, { headers: getHeaders(false) });
    return await res.json();
}

/**
 * Reset all usage analytics data
 */
export async function resetUsageData() {
    const res = await apiFetch(`${API_BASE}/models/usage`, {
        method: 'DELETE',
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch model pricing data for cost estimation
 */
export async function fetchModelPricing() {
    const res = await apiFetch(`${API_BASE}/models/pricing`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch active container tokens (admin only)
 */
export async function fetchContainers() {
    const res = await apiFetch(`${API_BASE}/containers`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Record model usage
 * @param {string} modelId - Model identifier
 * @param {string} provider - Provider name (local, anthropic, openrouter)
 * @param {number} promptTokens - Number of input tokens
 * @param {number} completionTokens - Number of output tokens
 * @param {string|null} sessionId - Optional session ID
 * @param {boolean} success - Whether the call succeeded
 * @param {string} callType - Type of LLM call: "chat", "memory_extraction", "web_search_select", etc.
 */
export async function recordModelUsage(modelId, provider, promptTokens = 0, completionTokens = 0, sessionId = null, success = true, callType = 'chat') {
    const res = await apiFetch(`${API_BASE}/models/usage`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            model_id: modelId,
            provider: provider,
            prompt_tokens: promptTokens,
            completion_tokens: completionTokens,
            session_id: sessionId,
            success: success,
            call_type: callType
        })
    });
    return await res.json();
}

/**
 * Fetch startup defaults (agent, collection, model set via CLI)
 */
export async function fetchDefaults() {
    const res = await apiFetch(`${API_BASE}/defaults`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch books for an agent (narrator mode)
 */
export async function fetchAgentBooks(agentName) {
    const res = await apiFetch(`${API_BASE}/agents/${encodeURIComponent(agentName)}/books`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Fetch agent opening/required fields
 */
export async function fetchAgentOpening(agentName) {
    const res = await apiFetch(`${API_BASE}/agents/${encodeURIComponent(agentName)}/opening`, {
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Store a field value for an agent
 */
export async function storeFieldValue(agentName, key, value) {
    return await fetch(
        `${API_BASE}/agents/${encodeURIComponent(agentName)}/memory/field?key=${encodeURIComponent(key)}&value=${encodeURIComponent(value)}`,
        { method: 'POST', headers: getHeaders(false) }
    );
}

/**
 * Clear agent memory
 */
export async function clearAgentMemory(agentName) {
    const res = await apiFetch(`${API_BASE}/agents/${encodeURIComponent(agentName)}/memory`, {
        method: 'DELETE',
        headers: getHeaders(false)
    });
    return await res.json();
}

/**
 * Reset narrator progress for an agent
 */
export async function resetNarratorProgress(agentName) {
    return await fetch(`${API_BASE}/agents/${encodeURIComponent(agentName)}/narrator/reset`, {
        method: 'POST',
        headers: getHeaders(false)
    });
}

/**
 * Get narrator progress for an agent
 * @param {string} agentName - Name of the agent
 * @returns {Promise<object>} Narrator progress with current_book, positions, current_position
 */
export async function fetchNarratorProgress(agentName) {
    const res = await fetch(`${API_BASE}/agents/${encodeURIComponent(agentName)}/narrator/progress`, {
        headers: getHeaders(false)
    });
    if (res.ok) {
        return await res.json();
    }
    return null;
}

/**
 * Set narrator position for an agent (used for revert on delete)
 * @param {string} agentName - Name of the agent
 * @param {string} book - Book name
 * @param {string} kbPath - KB file path
 * @param {number} chunkId - Chunk ID to set
 * @returns {Promise<object>} Result with success status
 */
export async function setNarratorPosition(agentName, book, kbPath, chunkId) {
    const res = await fetch(`${API_BASE}/agents/${encodeURIComponent(agentName)}/narrator/position`, {
        method: 'POST',
        headers: getHeaders(true),
        body: JSON.stringify({
            book: book,
            kb_path: kbPath,
            chunk_id: chunkId
        })
    });
    if (res.ok) {
        return await res.json();
    }
    console.error('[API] Failed to set narrator position:', res.status);
    return null;
}

/**
 * Fetch citations for a response (async, runs after response completes)
 * @param {object} options - Citation options
 * @param {string} options.agent - Agent name (or null if using collection)
 * @param {string} options.collection - Collection folder name (or null if using agent)
 * @param {string} options.question - User's original question
 * @param {string} options.response - The LLM's response text
 * @param {string} options.sessionId - Session ID for saving citations to database
 * @returns {Promise<string[]>} List of citation strings
 */
export async function fetchCitations({ agent, collection, question, response, sessionId }) {
    try {
        const res = await apiFetch(`${API_BASE}/chat/citations`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({
                agent: agent || undefined,
                collection: collection || undefined,
                question: question,
                response: response,
                session_id: sessionId
            })
        });
        const data = await res.json();
        return data.citations || [];
    } catch (e) {
        console.error('[fetchCitations] Error:', e);
        return [];
    }
}

/**
 * Load an agent with progress streaming
 * @param {string} agentName - Agent to load
 * @param {function} onProgress - Callback for progress updates (loaded, total, status)
 * @param {string} model - Optional model to warm (e.g. 'qwen2.5:1.5b')
 * @returns {Promise<object>} Final agent data
 */
export async function loadAgent(agentName, onProgress, model = null) {
    let url = `${API_BASE}/agents/${encodeURIComponent(agentName)}/load`;
    if (model) {
        url += `?model=${encodeURIComponent(model)}`;
    }
    const res = await fetch(url, {
        method: 'POST',
        headers: getHeaders(false)
    });

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalData = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
            if (line.startsWith('data: ')) {
                try {
                    const data = JSON.parse(line.slice(6));

                    if (data.name !== undefined) {
                        // Complete event (has 'name' field)
                        finalData = data;
                    } else if (data.total !== undefined && data.loaded === undefined) {
                        // Start event
                        onProgress?.(0, data.total, `Loading ${data.total} KBs...`);
                    } else if (data.loaded !== undefined) {
                        // Progress event
                        onProgress?.(data.loaded, data.total, data.status);
                    }
                } catch (e) {
                    console.error('[loadAgent] Parse error:', e);
                }
            }
        }
    }

    return finalData;
}

/**
 * Load a model into VRAM
 * @param {string} model - Model name or tier to load
 * @returns {Promise<object>} Model data when loaded
 */
export async function loadModel(model, workerId = null) {
    const url = `${API_BASE}/models/${encodeURIComponent(model)}/load`
        + (workerId != null ? `?worker_id=${workerId}` : '');
    const res = await fetch(url, {
        method: 'POST',
        headers: getHeaders(false)
    });

    if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'Failed to load model');
    }

    return await res.json();
}

/**
 * Load a collection with progress streaming
 * @param {string} collectionName - Collection name to load
 * @param {function} onProgress - Callback for progress updates (loaded, total, status)
 * @param {string} model - Optional model tier to warm (e.g. 'sonnet', 'medium')
 * @returns {Promise<object>} Final collection data
 */
export async function loadCollection(collectionName, onProgress, model = null) {
    let url = `${API_BASE}/collections/${encodeURIComponent(collectionName)}/load`;
    if (model) {
        url += `?model=${encodeURIComponent(model)}`;
    }
    const res = await fetch(url, {
        method: 'POST',
        headers: getHeaders(false)
    });

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let finalData = null;

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();

        for (const line of lines) {
            if (line.startsWith('data: ')) {
                try {
                    const data = JSON.parse(line.slice(6));

                    if (data.name !== undefined && data.loaded !== undefined) {
                        // Complete event (has 'name' field and 'loaded' flag)
                        finalData = data;
                    } else if (data.total !== undefined && data.loaded === undefined) {
                        // Start event
                        onProgress?.(0, data.total, `Loading ${data.total} KBs...`);
                    } else if (data.loaded !== undefined && data.name === undefined) {
                        // Progress event (has loaded but not name)
                        onProgress?.(data.loaded, data.total, data.status);
                    }
                } catch (e) {
                    console.error('[loadCollection] Parse error:', e);
                }
            }
        }
    }

    return finalData;
}

/**
 * Send a chat message with streaming response
 * @param {object} options - Chat options
 * @param {function} onChunk - Callback for each text chunk
 * @param {function} onComplete - Callback when complete with full data
 * @param {function} onError - Callback when streaming error occurs (optional)
 * @param {function} onSearchStatus - Callback for web search status updates (optional)
 * @param {function} onToolStatus - Callback for tool execution status updates (optional)
 * @param {function} onToolApproval - Callback for HITL tool approval requests (optional)
 * @param {function} onVisionStatus - Callback for vision pre-processing status updates (optional)
 * @param {function} onDocReady - Callback when document creation completes (optional)
 */
export async function sendChatMessage(options, onChunk, onComplete, onError, onSearchStatus, onToolStatus, onToolApproval, onVisionStatus, onDocReady, onDocStatus, onThinkingChunk) {
    const {
        message,
        sessionId,
        model,
        agent,
        collection,
        temperature,
        fidelity,
        memoryLevel,
        debugMode,
        webSearchEnabled,
        webSearchDepth,
        webSearchImages,
        narratorMode,
        narratorState,
        initialPrompt = false,
        attachments = null,
        kbThreshold,
        disabledTools
    } = options;

    const body = {
        message,
        stream: true,
        session_id: sessionId || undefined,
        model: model || undefined,
        agent: agent || undefined,
        collection: collection || undefined,
        temperature: temperature !== null ? temperature : undefined,
        fidelity: fidelity !== null ? fidelity : undefined,
        memory_level: memoryLevel !== undefined ? memoryLevel : 2,  // Default to Full
        debug: debugMode,
        web_search: webSearchEnabled || false,
        web_search_depth: webSearchDepth || 'surface',
        web_search_images: webSearchImages || false,
        initial_prompt: initialPrompt,
        kb_threshold: kbThreshold || undefined,
        attachments: attachments || undefined,
        disabled_tools: disabledTools?.length ? disabledTools : undefined,
        subordinate_disabled_tools: getSubordinateDisabledTools() || undefined,
        disable_planning: state.planningEnabled === false ? true : undefined,
        auto_compact: state.autoCompact !== false,
        // The server takes the workspace from how the request authenticated,
        // never from here; sent only so older servers keep working.
        container_token: state.containerMode ? state.containerToken : undefined
    };

    // Add narrator mode params if active (skip when paused — use normal chat flow)
    if (narratorMode && !state.narratorPaused && agent) {
        body.narrator_mode = true;
        body.narrator_book = narratorState?.book;
        body.narrator_kb_path = narratorState?.kb_path;
        body.narrator_chunk_id = narratorState?.chunk_id;
        // Multi-chunk: verbatim uses chunk_batch, non-verbatim uses narration_length
        const isVerbatim = state.agentSettings?.narrator_verbatim || false;
        const lengthChunks = [1, 3, 5, 10];
        body.narrator_chunk_count = isVerbatim
            ? (narratorState?.chunk_batch || 1)
            : lengthChunks[narratorState?.narration_length ?? 1];
    }

    // Use /chat/agent for agents, collections, web search, attachments, or existing sessions
    // The basic /chat endpoint doesn't support attachments or historical context with attachments
    // When continuing a session, we need /chat/agent to properly format historical attachments for LLM
    const endpoint = (agent || collection || webSearchEnabled || attachments || sessionId) ? '/chat/agent' : '/chat';
    const res = await fetch(`${API_BASE}${endpoint}`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(body)
    });

    // Check for HTTP errors before trying to read stream
    if (!res.ok) {
        let errorMsg = `Server error: ${res.status}`;
        try {
            const errorData = await res.json();
            errorMsg = errorData.detail || errorData.error || errorMsg;
        } catch {
            // Response might not be JSON
            try {
                errorMsg = await res.text() || errorMsg;
            } catch {
                // Use default error message
            }
        }
        console.error('[Chat] HTTP error:', res.status, errorMsg);
        onError?.(errorMsg, '');
        return;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = null;  // Track current SSE event type
    let accumulatedText = '';  // Track all received text for error recovery
    let completedSuccessfully = false;

    // Stream read timeout — detect stalled connections. Measures the gap
    // BETWEEN chunks, not total response time, so this is mostly about
    // catching genuinely dead streams without killing slow-but-alive ones.
    //
    // Local models on CPU (the prod target) can go many minutes between
    // chunks during multi-step pipelines — note synthesis chains 5-10
    // LLM calls together (map per entity, reduce, coverage verify,
    // chronological reformat) with effectively zero SSE output between
    // them. A 30-minute ceiling covers even very large note libraries
    // without abandoning the user if Ollama actually wedges.
    //
    // Remote providers (Anthropic / Groq / OpenRouter) stream tokens
    // continuously so 90s of silence really does mean dead, not slow.
    //
    // The tight budget belongs to the NAMED cloud providers, not to
    // "anything not labelled local". A crew-served model is not in the
    // model dropdown's tier list, so it never got the 'local' label and
    // inherited the cloud budget instead — which killed a working local
    // vision turn at exactly 90s while the image was still being
    // described. Anything running on our own hardware, whatever it is
    // called, gets the generous ceiling; only a known cloud lane gets the
    // strict one.
    const CLOUD_PROVIDERS = new Set(['anthropic', 'openai', 'groq', 'openrouter', 'google']);
    const STREAM_TIMEOUT_MS = CLOUD_PROVIDERS.has(state.loadedProvider) ? 90_000 : 1_800_000;
    let streamTimer = null;
    const resetStreamTimer = () => {
        if (streamTimer) clearTimeout(streamTimer);
        streamTimer = setTimeout(() => {
            console.error('[Chat] Stream read timeout — no data for', STREAM_TIMEOUT_MS / 1000, 'seconds');
            reader.cancel();
        }, STREAM_TIMEOUT_MS);
    };
    const clearStreamTimer = () => {
        if (streamTimer) { clearTimeout(streamTimer); streamTimer = null; }
    };
    resetStreamTimer();

    try {
        while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            resetStreamTimer();
            buffer += decoder.decode(value, { stream: true });
            const lines = buffer.split('\n');
            buffer = lines.pop();

            for (const line of lines) {
                // Track event type (SSE format: "event: type" followed by "data: json")
                if (line.startsWith('event: ')) {
                    currentEvent = line.slice(7).trim();
                    continue;
                }

                if (line.startsWith('data: ')) {
                    let data;
                    try {
                        data = JSON.parse(line.slice(6));
                    } catch (e) {
                        // Ignore JSON parse errors (malformed SSE data)
                        currentEvent = null;
                        continue;
                    }

                    try {
                        // Handle error events from server
                        if (currentEvent === 'error') {
                            console.error('[Chat] Streaming error from server:', data);
                            onError?.(data.error || 'Unknown streaming error', data.partial_text || accumulatedText, data.session_id || null, !!data.busy);
                            currentEvent = null;
                            return;  // Don't process further after error
                        }

                        // Handle web search status events
                        if (currentEvent === 'search_status') {
                            onSearchStatus?.(data);
                            currentEvent = null;
                            continue;
                        }

                        // Handle tool execution status events
                        if (currentEvent === 'tool_status') {
                            onToolStatus?.(data);
                            currentEvent = null;
                            continue;
                        }

                        // Handle tool progress events (real-time updates during tool loop)
                        if (currentEvent === 'tool_progress') {
                            onToolStatus?.(data);  // Reuse same callback
                            currentEvent = null;
                            continue;
                        }

                        // Handle HITL tool approval requests
                        if (currentEvent === 'tool_approval') {
                            onToolApproval?.(data);
                            currentEvent = null;
                            continue;
                        }

                        // Handle chain execution events (forward as tool status)
                        if (currentEvent === 'chain_progress' || currentEvent === 'chain_step_complete' || currentEvent === 'chain_result') {
                            onToolStatus?.(data);
                            currentEvent = null;
                            continue;
                        }

                        // Handle vision pre-processing status events
                        if (currentEvent === 'vision_status') {
                            onVisionStatus?.(data);
                            currentEvent = null;
                            continue;
                        }

                        // Handle doc_status events (intent detection progress)
                        if (currentEvent === 'doc_status') {
                            onDocStatus?.(data);
                            currentEvent = null;
                            continue;
                        }

                        // Handle doc_ready events (document created from response)
                        if (currentEvent === 'doc_ready') {
                            onDocReady?.(data);
                            currentEvent = null;
                            continue;
                        }

                        // Handle inter-step thinking chunks from tool loops
                        if (currentEvent === 'thinking_chunk') {
                            onThinkingChunk?.(data.chunk);
                            currentEvent = null;
                            continue;
                        }

                        // A compaction this turn waited for (context-meter.js)
                        if (currentEvent === 'compaction') {
                            document.dispatchEvent(new CustomEvent('ragsmith:compaction', { detail: data }));
                            currentEvent = null;
                            continue;
                        }

                        // Handle narrator mode lesson context events (with debug info)
                        if (currentEvent === 'lesson_context') {
                            currentEvent = null;
                            continue;
                        }

                        // Handle stream retract — text that was speculatively
                        // streamed during the tool loop turned out to be
                        // inter-step reasoning. Clear it from the client.
                        if (currentEvent === 'stream_retract') {
                            accumulatedText = '';
                            onChunk?.(null);  // null signals retract
                            currentEvent = null;
                            continue;
                        }

                        if (data.chunk) {
                            accumulatedText += data.chunk;
                            onChunk?.(data.chunk);
                        }

                        if (data.full_text !== undefined) {
                            completedSuccessfully = true;
                            onComplete?.(data);
                            if (data.context) {
                                document.dispatchEvent(new CustomEvent('ragsmith:context', { detail: data.context }));
                            }
                        }
                    } catch (e) {
                        console.error('[Chat] Error processing SSE event:', currentEvent, e);
                    }
                    currentEvent = null;  // Reset after processing data
                }
            }
        }

        clearStreamTimer();

        // Process any remaining data in buffer (final chunk might not end with \n)
        if (buffer.trim()) {
            if (buffer.startsWith('data: ')) {
                let data;
                try {
                    data = JSON.parse(buffer.slice(6));
                } catch (e) {
                    // Ignore JSON parse errors
                }
                if (data) {
                    if (data.chunk) {
                        accumulatedText += data.chunk;
                        onChunk?.(data.chunk);
                    }
                    if (data.full_text !== undefined) {
                        completedSuccessfully = true;
                        onComplete?.(data);
                        if (data.context) {
                            document.dispatchEvent(new CustomEvent('ragsmith:context', { detail: data.context }));
                        }
                    }
                }
            }
        }

        // Stream ended - check if it completed successfully
        if (!completedSuccessfully) {
            // Stream ended without sending full_text - likely a server-side error
            if (accumulatedText) {
                // Got some text but no completion - show what we have with error
                onError?.('Response ended unexpectedly', accumulatedText);
            } else {
                // Got nothing - something went wrong early
                onError?.('No response received from server', '');
            }
        }
    } catch (e) {
        clearStreamTimer();
        // Stream was interrupted (connection died, server crashed, timeout, etc.)
        console.error('[Chat] Stream interrupted:', e);
        if (!completedSuccessfully) {
            onError?.('Connection lost during response', accumulatedText);
        }
    }
}

/**
 * Check if a message is an instruction field trigger
 * @param {string} agent - Agent name
 * @param {string} message - User's message to check
 * @returns {Promise<{is_trigger: boolean, instruction_field?: object, table_schema?: object}>}
 */
export async function checkTrigger(agent, message) {
    const res = await apiFetch(
        `${API_BASE}/agents/${encodeURIComponent(agent)}/check-trigger?message=${encodeURIComponent(message)}`,
        { headers: getHeaders(false) }
    );
    return await res.json();
}

/**
 * Submit a response to an instruction field
 * @param {string} agent - Agent name
 * @param {string} instructionKey - The instruction field key
 * @param {string} userResponse - User's response text
 * @param {string} sessionId - Optional session ID to record in conversation
 * @returns {Promise<{success: boolean, extracted?: object, stored?: object, confirmation?: string, error?: string}>}
 */
export async function submitInstructionResponse(agent, instructionKey, userResponse, sessionId = null) {
    const res = await apiFetch(`${API_BASE}/agents/${encodeURIComponent(agent)}/instruction-response`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            instruction_key: instructionKey,
            user_response: userResponse,
            session_id: sessionId
        })
    });
    return await res.json();
}

// ============================================================================
// Web Search API
// ============================================================================

/**
 * Perform a web search
 * @param {string} query - Search query
 * @param {object} options - Search options
 * @param {number} options.maxResults - Max results (default 10)
 * @param {boolean} options.fetchContent - Whether to fetch full page content
 * @param {number} options.fetchTopN - How many top results to fetch content from
 * @param {string} options.sessionId - Optional session ID for tracking
 * @returns {Promise<{query: string, search_results: Array, fetched_content: Array}>}
 */
export async function webSearch(query, options = {}) {
    const res = await fetch(`${API_BASE}/search`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
            query,
            max_results: options.maxResults || 10,
            fetch_content: options.fetchContent || false,
            fetch_top_n: options.fetchTopN || 3,
            session_id: options.sessionId || null
        })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: 'Web search failed' }));
        throw new Error(error.detail || 'Web search failed');
    }
    return await res.json();
}

/**
 * Fetch and extract content from a URL
 * @param {string} url - URL to fetch
 * @param {boolean} useCache - Whether to use cached content (default true)
 * @returns {Promise<{url: string, title: string, content: string, success: boolean}>}
 */
export async function fetchUrl(url, useCache = true) {
    const res = await fetch(`${API_BASE}/search/fetch`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ url, use_cache: useCache })
    });
    if (!res.ok) {
        const error = await res.json().catch(() => ({ detail: 'Fetch failed' }));
        throw new Error(error.detail || 'Fetch failed');
    }
    return await res.json();
}

/**
 * Get web search statistics
 * @returns {Promise<{total_searches: number, cached_pages: number, active_search_cache: number}>}
 */
export async function getSearchStats() {
    const res = await fetch(`${API_BASE}/search/stats`, {
        headers: getHeaders(false)
    });
    if (!res.ok) {
        return { total_searches: 0, cached_pages: 0, active_search_cache: 0 };
    }
    return await res.json();
}

// ============================================================================
// Session Memory API
// ============================================================================

/**
 * Check if fact extraction is currently running for a session
 * @param {string} sessionId - Session ID to check
 * @returns {Promise<{extracting: boolean}>}
 */
export async function checkExtractionStatus(sessionId) {
    if (!sessionId) return { extracting: false };

    try {
        const res = await fetch(`${API_BASE}/sessions/${sessionId}/extraction_status`, {
            headers: getHeaders(false)
        });
        if (!res.ok) {
            return { extracting: false };
        }
        return await res.json();
    } catch {
        return { extracting: false };
    }
}

/**
 * Save an error message to a session (for retry persistence)
 * @param {string} sessionId - The session ID
 * @param {string} content - The error message content
 * @param {string} model - Optional model name
 * @returns {Promise<{success: boolean, message_id?: number}>}
 */
export async function saveErrorMessage(sessionId, content, model = null, userTurn = null) {
    if (!sessionId) return { success: false };

    try {
        const body = { content };
        if (model) body.model = model;
        // The turn being errored travels with the error: a failure during
        // media pre-processing dies before the server persists the user
        // message, and a redo has already deleted the old pair by then — so
        // without this, refresh shows an error bubble with no prompt, no
        // attachment, and nothing left to redo.
        if (userTurn) {
            body.user_content = userTurn.message ?? '';
            if (userTurn.attachments && userTurn.attachments.length) {
                body.user_attachments = userTurn.attachments;
            }
        }

        const res = await fetch(`${API_BASE}/sessions/${sessionId}/error`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify(body)
        });
        if (!res.ok) {
            return { success: false };
        }
        return await res.json();
    } catch {
        return { success: false };
    }
}

// ============================================================================
// Container Mode API
// ============================================================================

/**
 * Validate a container token and get its configuration
 * @param {string} token - The container token from URL
 * @returns {Promise<{valid: boolean, token?: string, agent?: string, collection?: string, model?: string, error?: string}>}
 */
export async function postToolApproval(approvalId, decision) {
    try {
        const res = await apiFetch(`${API_BASE}/chat/tool-approval`, {
            method: 'POST',
            headers: getHeaders(),
            body: JSON.stringify({ approval_id: approvalId, decision })
        });
        return await res.json();
    } catch (e) {
        console.error('[postToolApproval] Error:', e);
        return { status: 'error', error: e.message };
    }
}

/**
 * Cancel the current tool loop execution
 */
export async function cancelChat() {
    try {
        const res = await apiFetch(`${API_BASE}/chat/cancel`, {
            method: 'POST',
            headers: getHeaders()
        });
        return await res.json();
    } catch (e) {
        console.error('[cancelChat] Error:', e);
        return { status: 'error', error: e.message };
    }
}

export async function validateContainerToken(token) {
    try {
        const res = await apiFetch(`${API_BASE}/container/validate?u=${encodeURIComponent(token)}`);
        return await res.json();
    } catch (e) {
        console.error('[validateContainerToken] Error:', e);
        return { valid: false, error: 'Failed to validate container token' };
    }
}

// ── Capsule API ─────────────────────────────────────────────────

export async function validateCapsuleToken(token) {
    try {
        const res = await apiFetch(`${API_BASE}/capsule/validate?c=${encodeURIComponent(token)}`);
        return await res.json();
    } catch (e) {
        console.error('[validateCapsuleToken] Error:', e);
        return { valid: false, error: 'Failed to validate capsule token' };
    }
}

export async function createCapsule(sessionId, opts = {}) {
    const res = await apiFetch(`${API_BASE}/capsules`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ session_id: sessionId, ...opts }),
    });
    return await res.json();
}

export async function listCapsules(sessionId) {
    const res = await apiFetch(`${API_BASE}/capsules/${encodeURIComponent(sessionId)}`, {
        headers: getHeaders(false),
    });
    return await res.json();
}

export async function updateCapsule(capsuleId, opts) {
    const res = await apiFetch(`${API_BASE}/capsules/${capsuleId}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(opts),
    });
    return await res.json();
}

export async function deleteCapsule(capsuleId) {
    const res = await apiFetch(`${API_BASE}/capsules/${capsuleId}`, {
        method: 'DELETE',
        headers: getHeaders(false),
    });
    return await res.json();
}

export async function getCapsuleConversation(token) {
    const res = await apiFetch(`${API_BASE}/capsule/${encodeURIComponent(token)}/conversation`);
    return await res.json();
}


/**
 * How full a conversation's next turn is (the context wheel).
 */
export async function fetchSessionContext(sessionId, { model, memoryLevel } = {}) {
    const qs = new URLSearchParams();
    if (model) qs.set('model', model);
    if (memoryLevel !== undefined && memoryLevel !== null) qs.set('memory_level', String(memoryLevel));
    const q = qs.toString();
    const res = await apiFetch(`${API_BASE}/sessions/${encodeURIComponent(sessionId)}/context${q ? '?' + q : ''}`, {
        headers: getHeaders(false)
    });
    return res.json();
}

/**
 * Fold a conversation's older turns into a summary now.
 */
export async function compactSession(sessionId, { focus = '', model } = {}) {
    const res = await apiFetch(`${API_BASE}/sessions/${encodeURIComponent(sessionId)}/compact`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ focus, model: model || undefined })
    });
    return res.json();
}
